import {
	isLocalDevHostname,
	normalizePublicSiteBaseUrl,
} from "./resolve-public-site-url";

const TRAILING_SLASH_RE = /\/+$/;
const ABSOLUTE_HTTP_URL_RE = /^https?:\/\//i;
/** Last path segment looks like a file (`sitemap.xml`, `robots.txt`). */
const FILE_SEGMENT_EXT_RE = /\.[a-z0-9]{1,12}$/i;

export type AbsolutePublicUrlOptions = {
	/** Site SEO origin (e.g. `https://example.com` or `http://localhost:3003`). Used when `NEXT_PUBLIC_DOMAIN` is unset. */
	siteOrigin?: string | null;
	/** Override deployment base path (defaults to `NEXT_PUBLIC_BASE_PATH`). */
	deploymentBasePath?: string;
	/** Override public hostname (defaults to `NEXT_PUBLIC_DOMAIN`). */
	publicDomain?: string;
	/**
	 * When true, content URLs end with `/`. Default false = strip (existing tenants).
	 * Missing/undefined is treated as false. File-like paths (`.xml`, etc.) never gain a slash.
	 */
	trailingSlash?: boolean;
};

function trimString(value: string | undefined | null): string {
	return value?.trim() ?? "";
}

function readEnvString(key: string): string {
	const raw = process.env[key];
	if (raw === undefined || raw === null) {
		return "";
	}
	const trimmed = raw.trim();
	if (trimmed === "undefined" || trimmed === "null") {
		return "";
	}
	return trimmed;
}

function readConfiguredString(
	override: string | undefined,
	envKey: string
): string {
	if (override !== undefined) {
		return trimString(override);
	}
	return readEnvString(envKey);
}

/**
 * Normalizes a public path segment: leading slash, collapsed slashes, no trailing slash (except `/`).
 * Internal route matching always uses this strip form; preference applies only when emitting URLs.
 */
export function normalizePublicPath(path: string): string {
	const collapsed = path.trim().replace(/\/+/g, "/");
	const withSlash = collapsed.startsWith("/") ? collapsed : `/${collapsed}`;
	if (withSlash.length > 1 && withSlash.endsWith("/")) {
		return withSlash.slice(0, -1);
	}
	return withSlash;
}

/**
 * Region deployment prefix from `NEXT_PUBLIC_BASE_PATH` (e.g. `/uk`, or `""` on global root).
 */
export function getDeploymentBasePath(override?: string): string {
	const raw = readConfiguredString(override, "NEXT_PUBLIC_BASE_PATH");
	if (raw === "") {
		return "";
	}
	const normalized = raw.startsWith("/") ? raw : `/${raw}`;
	return normalized.replace(TRAILING_SLASH_RE, "");
}

/**
 * Public hostname from `NEXT_PUBLIC_DOMAIN` (hostname only, e.g. `example.com`).
 */
export function getPublicDomain(override?: string): string {
	return readConfiguredString(override, "NEXT_PUBLIC_DOMAIN").toLowerCase();
}

function resolveProtocolForHost(hostname: string): "http" | "https" {
	return isLocalDevHostname(hostname) ? "http" : "https";
}

function resolveOriginFromDomain(domain: string): string | null {
	if (!domain) {
		return null;
	}
	const hostname = domain.split(":")[0] ?? domain;
	return `${resolveProtocolForHost(hostname)}://${domain}`;
}

function resolveOriginFromSiteUrl(siteOrigin: string): string | null {
	const trimmed = siteOrigin.replace(TRAILING_SLASH_RE, "");
	if (!trimmed) {
		return null;
	}
	try {
		const parsed = new URL(trimmed);
		if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
			return null;
		}
		return normalizePublicSiteBaseUrl(trimmed);
	} catch {
		return null;
	}
}

function resolvePublicOrigin(
	options?: AbsolutePublicUrlOptions
): string | null {
	const domainOrigin = resolveOriginFromDomain(
		getPublicDomain(options?.publicDomain)
	);
	if (domainOrigin) {
		return domainOrigin;
	}
	return resolveOriginFromSiteUrl(trimString(options?.siteOrigin));
}

function isFileLikePath(pathname: string): boolean {
	const normalized = normalizePublicPath(pathname);
	if (normalized === "/") {
		return false;
	}
	const last = normalized.split("/").pop() ?? "";
	return FILE_SEGMENT_EXT_RE.test(last);
}

/**
 * Strips a trailing slash from absolute public URLs (`https://…/blogs/` → `https://…/blogs`).
 * Relative paths are returned unchanged.
 */
export function stripTrailingSlashFromAbsolutePublicUrl(url: string): string {
	if (!ABSOLUTE_HTTP_URL_RE.test(url)) {
		return url;
	}
	return url.replace(TRAILING_SLASH_RE, "");
}

/**
 * Applies site-wide trailing-slash preference to an absolute (or relative) public URL.
 * Root with preference true becomes `…/` (or `/`); file-like paths stay strip-form.
 */
export function applyTrailingSlashPreference(
	url: string,
	trailingSlash: boolean
): string {
	if (!trailingSlash) {
		if (!ABSOLUTE_HTTP_URL_RE.test(url)) {
			return normalizePublicPath(url);
		}
		return stripTrailingSlashFromAbsolutePublicUrl(url);
	}

	if (!ABSOLUTE_HTTP_URL_RE.test(url)) {
		const normalized = normalizePublicPath(url);
		if (normalized === "/") {
			return "/";
		}
		if (isFileLikePath(normalized)) {
			return normalized;
		}
		return `${normalized}/`;
	}

	const stripped = stripTrailingSlashFromAbsolutePublicUrl(url);
	try {
		const abs = new URL(stripped);
		if (isFileLikePath(abs.pathname)) {
			return stripped;
		}
		return `${stripped}/`;
	} catch {
		return stripped;
	}
}

/**
 * Preferred pathname for Region redirects given the site preference.
 * Returns `null` when the request path already matches (or should be skipped).
 * Preserves leading deployment prefixes; caller must skip API/static/reserved routes.
 */
export function preferredTrailingSlashPathname(
	pathname: string,
	trailingSlash: boolean
): string | null {
	if (!pathname || pathname === "/") {
		return null;
	}

	const hasTrailing = pathname.length > 1 && pathname.endsWith("/");
	const withoutTrailing = hasTrailing
		? pathname.replace(TRAILING_SLASH_RE, "")
		: pathname;
	if (!withoutTrailing || withoutTrailing === "/") {
		return null;
	}

	const last = withoutTrailing.split("/").pop() ?? "";
	if (FILE_SEGMENT_EXT_RE.test(last)) {
		return null;
	}

	if (trailingSlash) {
		return hasTrailing ? null : `${withoutTrailing}/`;
	}
	return hasTrailing ? withoutTrailing : null;
}

/**
 * Builds a fully-qualified public URL for the current region deployment.
 *
 * Format: `https://{domain}{deploymentBasePath}{path}` (http for local dev hosts).
 * Default (`trailingSlash` false/omitted): never ends with a trailing slash (legacy strip).
 * When `trailingSlash` is true: content URLs end with `/` (file-like paths stay strip).
 *
 * When `NEXT_PUBLIC_DOMAIN` is unset, falls back to `siteOrigin` from Site SEO
 * so existing single-origin deployments keep working with an empty base path.
 */
export function absolutePublicUrl(
	path: string,
	options?: AbsolutePublicUrlOptions
): string {
	const trailingSlash = options?.trailingSlash === true;
	const origin = resolvePublicOrigin(options);
	const deploymentBase = getDeploymentBasePath(options?.deploymentBasePath);
	const contentPath = normalizePublicPath(path);
	const pathSuffix = contentPath === "/" ? "" : contentPath;
	const combined = `${deploymentBase}${pathSuffix}`;
	const suffix = combined === "" ? "/" : combined;

	if (!origin) {
		return applyTrailingSlashPreference(suffix, trailingSlash);
	}

	const joined = `${origin}${suffix === "/" ? "" : suffix}`;
	const stripped = stripTrailingSlashFromAbsolutePublicUrl(joined);
	return applyTrailingSlashPreference(stripped, trailingSlash);
}
